import React, { useState } from 'react';

import {
  Box,
  Button,
  HStack,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { IconBadge } from '@saas-ui/react';
import { BookOpen } from 'lucide-react';

import { FONTS_OPACITY } from '@/constant/theme';
import type { DocumentSearchResult } from '@/hooks/useDocumentSearch';
import type { DocumentSearchPage } from '@/types/documents';

const SKELETON_COUNT = 3;

interface DocumentsSectionProps {
  results: DocumentSearchResult[];
  isLoading?: boolean;
  onSelect: (documentId: string, page?: number) => void;
}

export const DocumentsSection: React.FC<DocumentsSectionProps> = ({
  results,
  isLoading = false,
  onSelect,
}) => {
  if (!isLoading && results.length === 0) {
    return null;
  }

  return (
    <VStack align='stretch' gap={4} w='full' data-cy='global-search-documents'>
      <Stack gap={0} align='stretch'>
        <Text
          fontSize='md'
          fontWeight='semibold'
          color='tabs.font-selected'
          borderBottom='2px solid'
          borderColor='tabs.font-selected'
          w='fit-content'
          pb={2}
          mb={2}
        >
          Documents
        </Text>
        <SimpleGrid
          columns={{ base: 1, xl: 3 }}
          spacing={{ base: 2, xl: 2.5 }}
          w='full'
        >
          {isLoading && results.length === 0
            ? Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                <Skeleton
                  key={`documents-skeleton-${index}`}
                  height='72px'
                  rounded='2xl'
                  startColor='glass-items.bg-active'
                  endColor='cards.bg'
                />
              ))
            : results.map(result => (
                <DocumentResultCard
                  key={result.documentId}
                  result={result}
                  onSelect={onSelect}
                />
              ))}
        </SimpleGrid>
      </Stack>
    </VStack>
  );
};

interface DocumentResultCardProps {
  result: DocumentSearchResult;
  onSelect: (documentId: string, page?: number) => void;
}

/**
 * Name, a quiet "Library · category" line, then the best page line. Further
 * page rows sit behind "+N more pages" so a card stays about three lines.
 * There is no passage subtitle: FSI's passage is usually the document's
 * navigation guide in markdown, which reads as noise here.
 */
const DocumentResultCard: React.FC<DocumentResultCardProps> = ({
  result,
  onSelect,
}) => {
  const [showAllPages, setShowAllPages] = useState(false);
  const [bestPage, ...morePages] = result.pages;
  const visiblePages = showAllPages ? result.pages : bestPage ? [bestPage] : [];
  const hiddenCount = showAllPages ? 0 : morePages.length;

  return (
    <Stack
      gap={0}
      rounded='2xl'
      bg='cards.bg'
      border='1px solid'
      borderColor='border.light'
      overflow='hidden'
      minW={0}
      data-cy='global-search-document'
      data-document-id={result.documentId}
    >
      <Box
        as='button'
        type='button'
        textAlign='left'
        w='full'
        px={4}
        py={3}
        color='white'
        cursor='pointer'
        _hover={{ bg: 'cards.bg-hover' }}
        onClick={() => onSelect(result.documentId, result.bestPage)}
        data-cy='global-search-document-open'
      >
        <HStack align='center' gap={2.5} w='full' minW={0}>
          <Box flexShrink={0}>
            <IconBadge
              icon={<BookOpen width='16px' height='16px' />}
              variant='solid'
              colorScheme='whiteAlpha'
              isRound
              boxSize='36px'
              backgroundColor='glass-items.bg-active'
            />
          </Box>
          <Stack flex={1} gap={0.5} minW={0} maxW='100%'>
            <Text
              fontSize='sm'
              lineHeight='5'
              fontWeight='normal'
              noOfLines={1}
              data-cy='global-search-document-name'
            >
              {result.name}
            </Text>
            <HStack
              gap={1.5}
              minW={0}
              fontSize='xs'
              lineHeight='4'
              color='fonts.primary'
              opacity={FONTS_OPACITY}
            >
              <Text
                as='span'
                flexShrink={0}
                data-cy='global-search-document-badge'
              >
                Library
              </Text>
              {result.category ? (
                <>
                  <Text as='span' flexShrink={0} aria-hidden>
                    ·
                  </Text>
                  <Text
                    as='span'
                    noOfLines={1}
                    data-cy='global-search-document-category'
                  >
                    {result.category}
                  </Text>
                </>
              ) : null}
            </HStack>
            {!bestPage && result.summary ? (
              <Text
                fontSize='xs'
                lineHeight='4'
                opacity={FONTS_OPACITY}
                noOfLines={1}
                color='fonts.primary'
                data-cy='global-search-document-summary'
              >
                {result.summary}
              </Text>
            ) : null}
          </Stack>
        </HStack>
      </Box>
      {visiblePages.length > 0 ? (
        <Stack gap={0} borderTop='1px solid' borderColor='border.light'>
          {visiblePages.map(page => (
            <DocumentPageRow
              key={page.pageNumber}
              page={page}
              onClick={() => onSelect(result.documentId, page.pageNumber)}
            />
          ))}
          {hiddenCount > 0 ? (
            <Button
              variant='link'
              size='xs'
              alignSelf='flex-start'
              fontWeight='normal'
              color='link.font'
              px={4}
              pb={2}
              h='auto'
              onClick={() => setShowAllPages(true)}
              data-cy='global-search-document-more-pages'
            >
              {`+${hiddenCount} more ${hiddenCount === 1 ? 'page' : 'pages'}`}
            </Button>
          ) : null}
        </Stack>
      ) : null}
    </Stack>
  );
};

const DocumentPageRow: React.FC<{
  page: DocumentSearchPage;
  onClick: () => void;
}> = ({ page, onClick }) => (
  <Box
    as='button'
    type='button'
    textAlign='left'
    w='full'
    px={4}
    py={2}
    fontSize='xs'
    lineHeight='4'
    color='fonts.primary'
    cursor='pointer'
    _hover={{ bg: 'cards.bg-hover' }}
    onClick={onClick}
    data-cy='global-search-document-page'
    data-page-number={page.pageNumber}
  >
    <Text as='span' noOfLines={1}>
      {`p. ${page.pageNumber} · `}
      <HighlightedGuide guide={page.guide} words={page.matchedWords} />
    </Text>
  </Box>
);

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const HighlightedGuide: React.FC<{ guide: string; words?: string[] }> = ({
  guide,
  words,
}) => {
  const terms = (words ?? []).map(word => word.trim()).filter(Boolean);
  if (!terms.length) return <>{guide}</>;

  // Whole words only: "to" must not light up inside "Automatic".
  const alternatives = [...terms]
    .sort((a, b) => b.length - a.length)
    .map(escapeRegExp)
    .join('|');
  const pattern = new RegExp(`\\b(${alternatives})\\b`, 'gi');
  const lowered = new Set(terms.map(term => term.toLowerCase()));

  return (
    <>
      {guide.split(pattern).map((part, index) =>
        lowered.has(part.toLowerCase()) ? (
          <Box
            as='mark'
            key={index}
            bg='glass-items.bg-active'
            color='inherit'
            fontWeight='semibold'
            rounded='sm'
            px={0.5}
            data-cy='global-search-document-match'
          >
            {part}
          </Box>
        ) : (
          <React.Fragment key={index}>{part}</React.Fragment>
        )
      )}
    </>
  );
};
